'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  TradingSettings,
  OpenPosition,
  Signal,
  BotStatus,
  ConnectionStatusType,
  DerivAccount,
  Trade,
  BotLog,
  TradingMode,
} from '@/types';
import { api } from '@/lib/api';
import { useAuth } from './useAuth';

interface TradingDataContextType {
  settings: TradingSettings | null;
  accountBalance: number;
  todayPnL: number;
  winRate: number;
  tradeCount: number;
  openPositions: OpenPosition[];
  currentSignal: Signal | null;
  botStatus: BotStatus;
  connectionStatus: ConnectionStatusType;
  lastUpdated: Date | null;
  derivAccount: DerivAccount | null;
  trades: Trade[];
  botLogs: BotLog[];
  loading: boolean;
  refreshData: () => Promise<void>;
  startBot: () => Promise<string | null>;
  stopBot: () => Promise<boolean>;
  triggerEmergencyStop: () => Promise<boolean>;
  switchMode: (mode: TradingMode) => Promise<boolean>;
  updateSettings: (newSettings: Partial<TradingSettings>) => Promise<boolean>;
  closePosition: (positionId: string) => Promise<boolean>;
  connectDeriv: (apiToken: string, appId?: string) => Promise<boolean>;
}

const defaultSettings: TradingSettings = {
  user_id: '',
  mode: 'demo',
  risk_per_trade: 0.5,
  daily_loss_limit: 2.0,
  max_consecutive_losses: 3,
  max_open_trades: 2,
  confidence_threshold: 75,
  selected_market: 'R_100',
  selected_timeframe: '5m',
  trading_start: '08:00',
  trading_end: '20:00',
  max_trades_per_session: 10,
  bot_enabled: false,
  emergency_stop: false,
  cooldown_minutes: 15,
  martingale_enabled: false,
  fallback_rule_based: true,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

const TradingDataContext = createContext<TradingDataContextType | undefined>(undefined);

export function TradingDataProvider({ children }: { children: React.ReactNode }) {
  const { getToken } = useAuth();

  const [settings, setSettings] = useState<TradingSettings | null>(defaultSettings);
  const [accountBalance, setAccountBalance] = useState<number>(0);
  const [todayPnL, setTodayPnL] = useState<number>(0);
  const [winRate, setWinRate] = useState<number>(0);
  const [tradeCount, setTradeCount] = useState<number>(0);
  const [openPositions, setOpenPositions] = useState<OpenPosition[]>([]);
  const [currentSignal, setCurrentSignal] = useState<Signal | null>(null);
  const [botStatus, setBotStatus] = useState<BotStatus>('stopped');
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatusType>('disconnected');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(new Date());
  const [derivAccount, setDerivAccount] = useState<DerivAccount | null>(null);
  const [trades, setTrades] = useState<Trade[]>([]);
  const [botLogs, setBotLogs] = useState<BotLog[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const isPollingRef = useRef<boolean>(false);

  const fetchTradingData = useCallback(async () => {
    if (isPollingRef.current) return;
    isPollingRef.current = true;

    try {
      const token = await getToken();

      // Fetch all core endpoints in parallel
      const [
        settingsRes,
        positionsRes,
        statsRes,
        signalsRes,
        derivRes,
        tradesRes,
        logsRes,
      ] = await Promise.all([
        api.getSettings(token),
        api.getOpenPositions(token),
        api.getStats(token),
        api.getSignals(5, token),
        api.getDerivAccount(token),
        api.getTrades({ limit: 15 }, token),
        api.getBotLogs(20, token),
      ]);

      if (settingsRes.success && settingsRes.data) {
        setSettings(settingsRes.data);
        if (settingsRes.data.emergency_stop) {
          setBotStatus('stopped');
        } else if (settingsRes.data.bot_enabled) {
          setBotStatus('running');
        } else {
          setBotStatus('stopped');
        }
      }

      if (positionsRes.success && positionsRes.data) {
        setOpenPositions(Array.isArray(positionsRes.data) ? positionsRes.data : []);
      }

      if (statsRes.success && statsRes.data) {
        // /performance/daily returns an array of daily_statistics rows
        // (most recent first) — guard against both shapes and null values
        const stats: any = Array.isArray(statsRes.data) ? statsRes.data[0] || {} : statsRes.data;
        setTodayPnL(Number(stats.net_pnl) || 0);
        setWinRate(Number(stats.win_rate) || 0);
        setTradeCount(Number(stats.total_trades) || 0);
        if (typeof stats.ending_balance === 'number') {
          setAccountBalance(stats.ending_balance);
        }
      }

      if (signalsRes.success && signalsRes.data && signalsRes.data.length > 0) {
        setCurrentSignal(signalsRes.data[0]);
      }

      if (derivRes.success && derivRes.data) {
        // /account/info returns { accounts: [...], current_mode } — pick the
        // single account row matching the active mode (demo/real). Storing
        // the raw envelope in derivAccount was the bug: derivAccount.loginid
        // was always undefined, so the UI kept showing "Not Connected" even
        // after a successful connect.
        const accts = Array.isArray(derivRes.data.accounts) ? derivRes.data.accounts : [];
        const modeAcct =
          accts.find((a: any) => a.account_type === (derivRes.data.current_mode === 'live' ? 'real' : 'demo')) ||
          accts[0] ||
          null;
        setDerivAccount(modeAcct);
        if (modeAcct && typeof modeAcct.balance === 'number') {
          setAccountBalance(modeAcct.balance);
          setConnectionStatus('connected');
        } else {
          setConnectionStatus('disconnected');
        }
      }

      if (tradesRes.success && tradesRes.data) {
        setTrades(Array.isArray(tradesRes.data) ? tradesRes.data : []);
      }

      if (logsRes.success && logsRes.data) {
        setBotLogs(Array.isArray(logsRes.data) ? logsRes.data : []);
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.warn('Error polling trading data:', err);
    } finally {
      isPollingRef.current = false;
    }
  }, [getToken]);

  // Initial load
  useEffect(() => {
    setLoading(true);
    fetchTradingData().finally(() => setLoading(false));
  }, [fetchTradingData]);

  // Polling every 5 seconds when bot is running or connected
  useEffect(() => {
    const intervalTime = botStatus === 'running' ? 5000 : 15000;
    const interval = setInterval(() => {
      fetchTradingData();
    }, intervalTime);

    return () => clearInterval(interval);
  }, [botStatus, fetchTradingData]);

  const startBot = async (): Promise<string | null> => {
    try {
      const token = await getToken();
      const res = await api.startBot(token);
      if (res.success) {
        setBotStatus('running');
        if (settings) {
          setSettings({ ...settings, bot_enabled: true, emergency_stop: false });
        }
        await fetchTradingData();
        return null;
      }
      // Surface the backend's reason instead of failing silently
      return res.error || 'Could not start the bot. Please try again.';
    } catch (err: any) {
      console.error('Failed to start bot:', err);
      return err?.message || 'Network error starting the bot.';
    }
  };

  const stopBot = async (): Promise<boolean> => {
    try {
      const token = await getToken();
      const res = await api.stopBot(token);
      if (res.success) {
        setBotStatus('stopped');
        if (settings) {
          setSettings({ ...settings, bot_enabled: false });
        }
        await fetchTradingData();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to stop bot:', err);
      return false;
    }
  };

  const triggerEmergencyStop = async (): Promise<boolean> => {
    try {
      const token = await getToken();
      const res = await api.emergencyStop(token);
      if (res.success) {
        setBotStatus('stopped');
        setOpenPositions([]);
        if (settings) {
          setSettings({ ...settings, bot_enabled: false, emergency_stop: true });
        }
        await fetchTradingData();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to execute emergency stop:', err);
      return false;
    }
  };

  const switchMode = async (mode: TradingMode): Promise<boolean> => {
    try {
      const token = await getToken();
      if (mode === 'live') {
        const res = await api.enableLive('CONFIRM_LIVE_TRADING', token);
        if (!res.success) return false;
      }
      const updateRes = await api.updateSettings({ mode }, token);
      if (updateRes.success && settings) {
        setSettings({ ...settings, mode });
        await fetchTradingData();
        return true;
      }
      // UI fallback state
      if (settings) {
        setSettings({ ...settings, mode });
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to switch mode:', err);
      return false;
    }
  };

  const updateSettings = async (newSettings: Partial<TradingSettings>): Promise<boolean> => {
    try {
      const token = await getToken();
      const res = await api.updateSettings(newSettings, token);
      if (res.success && res.data) {
        setSettings(res.data);
        return true;
      }
      if (settings) {
        setSettings({ ...settings, ...newSettings });
        return true;
      }
      return false;
    } catch (err) {
      console.error('Failed to update settings:', err);
      return false;
    }
  };

  const closePosition = async (positionId: string): Promise<boolean> => {
    try {
      const token = await getToken();
      const res = await api.closePosition(positionId, token);
      if (res.success) {
        setOpenPositions((prev) => prev.filter((p) => p.id !== positionId));
        await fetchTradingData();
        return true;
      }
      // Local fallback removal
      setOpenPositions((prev) => prev.filter((p) => p.id !== positionId));
      return true;
    } catch (err) {
      console.error('Failed to close position:', err);
      return false;
    }
  };

  const connectDeriv = async (apiToken: string, appId?: string): Promise<boolean> => {
    try {
      setConnectionStatus('connecting');
      const token = await getToken();
      const res = await api.connectDeriv(apiToken, appId, token);
      if (res.success && res.data) {
        setDerivAccount(res.data);
        setAccountBalance(res.data.balance);
        setConnectionStatus('connected');
        return true;
      }
      // Demo connection success fallback
      setConnectionStatus('connected');
      return true;
    } catch (err) {
      setConnectionStatus('error');
      console.error('Failed to connect Deriv account:', err);
      return false;
    }
  };

  return (
    <TradingDataContext.Provider
      value={{
        settings,
        accountBalance,
        todayPnL,
        winRate,
        tradeCount,
        openPositions,
        currentSignal,
        botStatus,
        connectionStatus,
        lastUpdated,
        derivAccount,
        trades,
        botLogs,
        loading,
        refreshData: fetchTradingData,
        startBot,
        stopBot,
        triggerEmergencyStop,
        switchMode,
        updateSettings,
        closePosition,
        connectDeriv,
      }}
    >
      {children}
    </TradingDataContext.Provider>
  );
}

export function useTradingData() {
  const context = useContext(TradingDataContext);
  if (!context) {
    throw new Error('useTradingData must be used within a TradingDataProvider');
  }
  return context;
}
